import { hasRole } from "src/permissions";
import { ReleaseReservePopup } from "../popup/ReleaseReservePopup";
import { TransactionPopup } from "../TransactionPreview";
import type { IPaymentSession } from "src/types/types";
import { appLocale } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, ChevronLeft, ChevronRight, Download, RotateCw, X } from "lucide-react";
import styled from "styled-components";
import Panel from "@ui/panel/Panel";
import { Table } from "@ui/table/Table";
import { GD } from "src/GD";
import { loadRollingTransactionPage, buildRollingTransactionsCsv, formatRollingTransactionDate, getRollingTransactionsRequest, parseRollingTransactions, parseRollingTransactionCursorPage, ROLLING_TRANSACTIONS_EXPORT_PAGE_SIZE, type RollingReserveTransaction } from "./rollingReserveTransactions";
import { formatReserveAmount } from "./rollingReserveData";
import { CursorExportPopup } from "../../modules/cursorExport/CursorExportPopup";
import type { CursorExportJob } from "../../modules/cursorExport/cursorExport";

interface Props {
	clientId: number;
	currency: string;
	projectId: number;
	userId: number;
	onClose: () => void;
}

const columns: { field: Extract<keyof RollingReserveTransaction, string>; title: string; width: string }[] = [
	{ field: "id", get title() { return msg("dateId"); }, width: "minmax(145px, max-content)" },
	{ field: "mid", get title() { return msg("merchant"); }, width: "minmax(120px, 1fr)" },
	{ field: "channel_alias", get title() { return msg("channel"); }, width: "minmax(140px, 1fr)" },
	{ field: "amount", get title() { return msg("transactionAmount"); }, width: "max-content" },
	{ field: "rolling_reserve_amount", get title() { return msg("reserved_3385ffe6"); }, width: "max-content" },
	{ field: "reserve_release_date", get title() { return msg("releaseDate"); }, width: "max-content" },
];
type PageData = ReturnType<typeof parseRollingTransactions>;

const SelectionCheckbox = ({ checked, mixed = false, disabled, label, onChange }: { checked: boolean; mixed?: boolean; disabled: boolean; label: string; onChange: (checked: boolean) => void }) => {
	const ref = useRef<HTMLInputElement>(null);
	useEffect(() => { if (ref.current) ref.current.indeterminate = mixed; }, [mixed]);
	return <label style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 24, cursor: disabled ? "default" : "pointer" }} onClick={event => event.stopPropagation()}>
		<input ref={ref} type="checkbox" checked={checked} disabled={disabled} aria-label={label} aria-checked={mixed ? "mixed" : checked} onChange={event => onChange(event.target.checked)} style={{ width: 16, height: 16, margin: 0, accentColor: "#2563eb" }} />
	</label>;
};
const SelectionBar = styled.div`
	display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; flex-shrink: 0;
	font-size: 12px; color: #475467;
	button { min-height: 32px; padding: 6px 10px; background: #fff; }
	.hint { color: #98a2b3; margin-left: auto; }
`;

const DetailCell = styled.div`
	font-size: 13px; line-height: 1.5; min-width: 0; overflow-wrap: anywhere;
	small { display: block; font-size: 11px; color: #667085; }
`;

const ActionButton = styled.button.attrs({ type: "button" })`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	padding: 10px 16px;
	border: 1px solid #d0d5dd;
	border-radius: 6px;
	background: transparent;
	color: #344054;
	font: inherit;
	font-size: 13px;
	cursor: pointer;
	&:hover:not(:disabled) { background: #f5f3ff; }
	&:disabled { opacity: 0.5; cursor: default; }
	&:focus-visible { outline: 2px solid #6d5ce8; outline-offset: 2px; }
`;

const Header = styled.header`
	flex-shrink: 0; display: flex; align-items: flex-start; justify-content: space-between; gap: 20px;
	padding: 20px 24px; border-bottom: 1px solid #eaecf0; background: #fff;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	.eyebrow { font-size: 11px; color: #667085; margin-bottom: 5px; }
	h2 { margin: 0; font-size: 21px; font-weight: 600; line-height: 1.3; color: #182230; }
	button { width: 32px; height: 32px; padding: 0; border-color: transparent; }
`;
const Content = styled.div`
	flex: 1; min-height: 0; overflow: hidden; padding: 20px 24px;
	display: flex; flex-direction: column; gap: 16px; background: #f5f7fa;
	font: 13px/1.5 "Euclid Circular B", sans-serif; color: #344054;
	@media (max-width: 600px) { padding: 16px; }
`;
const TransactionLink = styled.button.attrs({ type: "button" })`
	border: 0; padding: 0; background: transparent; color: #175cd3; font: inherit; font-size: 11px; cursor: pointer;
	&:hover { text-decoration: underline; }
	&:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; border-radius: 2px; }
`;
const Summary = styled.div`
	padding: 14px 18px; background: #fff; border: 1px solid #d0d5dd; border-radius: 12px;
	box-shadow: 0 1px 2px rgba(16,24,40,.05); display: flex; align-items: center; gap: 12px 28px; flex-wrap: wrap; flex-shrink: 0;
	.context { color: #667085; font-size: 12px; }
	.as-of { margin-top: 3px; color: #98a2b3; font-size: 11px; }
	.metric { display: flex; flex-direction: column; gap: 3px; color: #667085; font-size: 11px; }
	strong { color: #182230; font-size: 16px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.actions { display: flex; gap: 8px; margin-left: auto; flex-wrap: wrap; }
	.actions button { min-height: 36px; padding: 8px 12px; }
	@media (max-width: 600px) { gap: 10px 20px; .context { flex-basis: 100%; } }
`;
const Paging = styled.nav`
	display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0;
	font-variant-numeric: tabular-nums;
	button { height: 36px; padding: 0 12px; border-radius: 8px; background: #fff; }
	.total { margin-left: auto; color: #667085; font-size: 12px; }
`;

export const RollingReserveTransactionsPopup = ({ clientId, currency, projectId, userId, onClose }: Props) => {
	const localeRevision = useLocale();
	const [currentProject] = GD.B_SELECTED_PROJECT.use();
	const [profile] = GD.REQ_PROFILE.useValue(undefined, undefined, [currentProject]);
	const canManage = currentProject?.id === projectId && profile?.id === userId && hasRole(profile.projects.find(item => item.id === projectId)?.roles, "admin");
	const [selection, setSelection] = useState<{ scope: string; ids: Set<number> }>({ scope: "", ids: new Set() });
	const [releaseIds, setReleaseIds] = useState<number[] | null>(null);
	const popup = useRef<HTMLDivElement>(null);
	const active = useRef(false);
	const transactionPopup = useRef<HTMLDivElement>(null);
	const tableArea = useRef<HTMLDivElement>(null);
	const [transaction, setTransaction] = useState<IPaymentSession | null>(null);
	const [page, setPage] = useState(0);
	const cursorCache = useRef<{ key: string; cursors: (string | undefined)[] }>({ key: "", cursors: [undefined] });
	const [attempt, setAttempt] = useState(0);
	const scopeKey = JSON.stringify([clientId, currency, projectId, userId]);
	const requestKey = JSON.stringify([clientId, currency, projectId, userId, page, attempt]);
	const [loaded, setLoaded] = useState<{ scopeKey: string; key: string; busy: boolean; data?: PageData; error?: string }>({ scopeKey, key: requestKey, busy: true });
	const state = loaded.scopeKey !== scopeKey ? { scopeKey, key: requestKey, busy: true }
		: loaded.key === requestKey ? loaded : { ...loaded, key: requestKey, busy: true, error: undefined };
	const [exportOpen, setExportOpen] = useState(false);
	const selected = canManage && selection.scope === scopeKey ? selection.ids : new Set<number>();
	const tableColumns = canManage ? [{ field: "__selection", title: "", width: "44px" }, ...columns] : columns;
	const pageIds = state.data?.rows.map(row => row.id) ?? [];
	const allSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
	const someSelected = pageIds.some(id => selected.has(id));
	const selectRows = (ids: number[], checked: boolean) => {
		if (!canManage || state.busy) return;
		setSelection(current => {
			const next = new Set(current.scope === scopeKey ? current.ids : []);
			ids.forEach(id => { if (checked) next.add(id); else next.delete(id); });
			return { scope: scopeKey, ids: next };
		});
	};
	const refresh = () => {
		setSelection({ scope: scopeKey, ids: new Set() });
		setPage(0); setAttempt(value => value + 1);
	};
	useEffect(() => { setSelection({ scope: scopeKey, ids: new Set() }); setReleaseIds(null); }, [scopeKey, canManage]);


	useEffect(() => {
		active.current = true;
		const previousFocus = document.activeElement;
		popup.current?.focus();
		const subscription = GD.S_AUTH_LOG_OUT.subscribe(() => { active.current = false; onClose(); });
		return () => {
			active.current = false;
			GD.S_AUTH_LOG_OUT.unsubscribe(subscription);
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
		};
	}, [onClose]);

	useEffect(() => {
		if (!transaction) return;
		const previousFocus = document.activeElement;
		transactionPopup.current?.focus();
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus(); };
	}, [transaction]);

	useEffect(() => { tableArea.current?.scrollTo({ top: 0 }); }, [page]);

	const openTransaction = (row: RollingReserveTransaction) => {
		if (!state.busy) setTransaction({ id: row.id } as IPaymentSession);
	};

	const checkContext = useCallback(async () => {
		const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
		if (!active.current || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
	}, [projectId, userId]);

	useEffect(() => {
		let cancelled = false;
		const abort = new AbortController();
		const cacheKey = JSON.stringify([scopeKey, attempt]);
		if (cursorCache.current.key !== cacheKey) cursorCache.current = { key: cacheKey, cursors: [undefined] };
		const cursors = cursorCache.current.cursors;
		setLoaded(previous => ({ scopeKey, key: requestKey, busy: true, data: previous.scopeKey === scopeKey ? previous.data : undefined }));
		const load = async () => {
			try {
				const data = await loadRollingTransactionPage(page, cursors, async cursor => {
					await checkContext();
					abort.signal.throwIfAborted();
					const response = await GD.REQ_API.request({ ...getRollingTransactionsRequest(clientId, currency, projectId, cursor), signal: abort.signal });
					await checkContext();
					if (response.error) throw new Error("Request failed");
					return parseRollingTransactions(response.data, clientId, currency);
				}, abort.signal);
				if (!cancelled) setLoaded({ scopeKey, key: requestKey, busy: false, data });
			} catch {
				if (!cancelled && active.current) setLoaded({ scopeKey, key: requestKey, busy: false, error: msg("couldNotLoadHeldTransactionsPleaseTryAgain") });
			}
		};
		void load();
		return () => { cancelled = true; abort.abort(); };
	}, [clientId, currency, projectId, page, attempt, scopeKey, requestKey, checkContext]);

	const exportJob = useMemo<CursorExportJob<RollingReserveTransaction>>(() => ({
		filename: `rolling_reserve_${clientId}_${currency}.csv`,
		loadPage: async (nextCursor, signal) => {
			signal.throwIfAborted();
			await checkContext();
			signal.throwIfAborted();
			const response = await GD.REQ_API.request({ ...getRollingTransactionsRequest(clientId, currency, projectId, nextCursor, ROLLING_TRANSACTIONS_EXPORT_PAGE_SIZE), signal });
			signal.throwIfAborted();
			await checkContext();
			if (response.error) throw new Error(msg("couldNotLoadTheNextBatchPleaseRestartTheExport"));
			return parseRollingTransactionCursorPage(response.data, clientId, currency);
		},
		getRowKey: row => row.id,
		buildFile: rows => new Blob([buildRollingTransactionsCsv(rows, currency)], { type: "text/csv;charset=utf-8;" }),
		beforeSave: checkContext,
	}), [localeRevision, clientId, currency, projectId, checkContext]);

	if (typeof document === "undefined") return null;
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={msg("templateHeldTransactions_c7ba0b51", { v0: currency })} tabIndex={-1}
		onKeyDown={event => {
			if (exportOpen || transaction || releaseIds) return;
			if (event.key !== "Tab") return;
			const controls = popup.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex="0"]');
			const first = controls?.[0];
			const last = controls?.[controls.length - 1];
			if (!first || !last) { event.preventDefault(); return; }
			if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
		}}>
		<Panel decorator="absolute" onClose={onClose} growContent disableScroll
			sx={{ padding: "0", borderRadius: "12px", backgroundColor: "#fff", width: "1200px", maxWidth: "94vw", height: "80dvh", maxHeight: "90dvh", overflow: "hidden" }}>
			<Header><div><div className="eyebrow">Rolling Reserve</div><h2>{msg("templateHeldTransactions_c7ba0b51", { v0: currency })}</h2></div><ActionButton onClick={onClose} aria-label={msg("close")}><X size={18} aria-hidden="true" /></ActionButton></Header>
			<Content>
				<Summary>
					<div className="context">
						<div>{msg("currentlyHeld_9e9bbd18")}{currency}{msg("allChannels")}</div>
						{state.data?.asOf && <div className="as-of">{msg("asOf")}{state.data.asOf}</div>}
					</div>
					{state.data?.summary && <>
						<div className="metric">{msg("held")}<strong>{formatReserveAmount(state.data.summary.held)} {currency}</strong></div>
						{state.data.summary.nextRelease && <div className="metric">{msg("nextRelease")}<strong>{state.data.summary.nextRelease}</strong></div>}
					</>}
					<div className="actions">
						<ActionButton disabled={state.busy} onClick={refresh}><RotateCw size={14} aria-hidden="true" />{state.error ? msg("retry") : msg("refresh")}</ActionButton>
						<ActionButton disabled={exportOpen || state.busy || !state.data?.rows.length} onClick={() => setExportOpen(true)}><Download size={14} aria-hidden="true" />{msg("downloadCsv")}</ActionButton>
					</div>
				</Summary>
				{canManage && <SelectionBar>
					<span aria-live="polite">{msg("reserveSelectedCount", { count: selected.size })}</span>
					<ActionButton disabled={state.busy || !selected.size} onClick={() => setReleaseIds([...selected])}><CalendarClock size={15} aria-hidden="true" />{msg("reserveChangeSelectedDate")}</ActionButton>
					{selected.size > 0 && <ActionButton disabled={state.busy} onClick={() => setSelection({ scope: scopeKey, ids: new Set() })}>{msg("reserveClearSelection")}</ActionButton>}
					<span className="hint">{msg("reserveSelectionAcrossPages")}</span>
				</SelectionBar>}
				<div role="status" aria-live="polite" aria-busy={state.busy} style={{ display: state.busy || state.error || state.data?.rows.length === 0 ? "block" : "none", lineHeight: "20px", fontSize: 13, flexShrink: 0 }}>
					{state.busy && <span>{msg("loadingTransactions_d69971d3")}</span>}
					{state.error && <span style={{ color: "#b42332" }}>{state.error}</span>}
					{!state.busy && state.data?.rows.length === 0 && <span>{msg("noHeldTransactionsOnThisPage")}</span>}
				</div>
				<div ref={tableArea} tabIndex={0} aria-label={msg("heldTransactionsTable")} aria-busy={state.busy} style={{ flex: "1 1 0", minHeight: 0, overflow: "auto", scrollbarGutter: "stable", border: "1px solid #d0d5dd", background: "#fff", boxShadow: "0 1px 2px rgba(16,24,40,.05)", borderRadius: 12, opacity: state.busy ? 0.45 : 1 }}>
					<Table<RollingReserveTransaction>
						onRowClick={state.busy ? undefined : openTransaction}
						headPadding="14px 12px" cellPadding="8px 12px"
						headSx={{ position: "sticky", top: "0", zIndex: 1, backgroundColor: "#f9fafb", borderBottom: "1px solid #eaecf0", color: "#667085" }}
						sx={{ backgroundColor: "#fff", width: "100%", minWidth: "1000px" }}
						headers={tableColumns.map(column => ({ value: column.field, gridColumn: column.width }))}
						order={tableColumns.map(column => column.field)}
						gridTemplateColumns={tableColumns.map(column => column.width).join(" ")}
						headerFactory={value => value === "__selection" ? { content: <SelectionCheckbox checked={allSelected} mixed={someSelected && !allSelected} disabled={state.busy || !pageIds.length} label={msg("reserveSelectPage")} onChange={checked => selectRows(pageIds, checked)} /> } : ({ content: <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{columns.find(column => column.field === value)?.title ?? value}</span> })}
						rowSx={row => canManage && selected.has(row.id) ? { "& > div": { backgroundColor: "#eff6ff" }, "&:hover > div": { backgroundColor: "#e0edff" } } : undefined}
						cellFactory={(row, _cell, _index, _key, name) => {
							if (name === "__selection") return { content: <SelectionCheckbox checked={selected.has(row.id)} disabled={state.busy} label={msg("reserveSelectTransaction", { id: row.id })} onChange={checked => selectRows([row.id], checked)} /> };
							if (name === "id") return { content: <DetailCell>{formatRollingTransactionDate(row.created_at)}<small><TransactionLink disabled={state.busy} onClick={event => { event.stopPropagation(); openTransaction(row); }} aria-label={msg("templatePayment_c88c6d69", { v0: row.id })}>{row.id}</TransactionLink></small></DetailCell> };
							if (name === "mid") return { content: <DetailCell>{row.mid_name ?? "—"}<small>{row.mid}</small></DetailCell> };
							if (name === "channel_alias") return { content: <DetailCell>{row.channel_alias ?? "—"}</DetailCell> };
							if (name === "amount") return { content: <DetailCell style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatReserveAmount(row.amount)} {row.currency}</DetailCell> };
							if (name === "rolling_reserve_amount") return { content: <DetailCell style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatReserveAmount(row.rolling_reserve_amount)} {row.currency}{row.rolling_reserver_percent !== undefined && row.rolling_reserver_percent !== null && <small>{row.rolling_reserver_percent}%</small>}</DetailCell> };
							return { content: <DetailCell>{formatRollingTransactionDate(row.reserve_release_date, true)}</DetailCell> };
						}}
						data={state.data?.rows ?? []}
					/>
				</div>
				<Paging aria-label={msg("pagination")} aria-busy={state.busy}>
					<ActionButton disabled={state.busy || page === 0} onClick={() => setPage(value => Math.max(0, value - 1))}><ChevronLeft size={16} aria-hidden="true" />{msg("previous")}</ActionButton>
					<span>{msg("templatePage_7cf4df60", { v0: page + 1 })}</span>
					<ActionButton disabled={state.busy || !state.data?.nextCursor} onClick={() => { if (!state.busy && state.data?.nextCursor) setPage(value => value + 1); }}>{msg("next")}<ChevronRight size={16} aria-hidden="true" /></ActionButton>
					{state.data && <span className="total">{state.data.total.toLocaleString(appLocale("en-GB"))}{msg("transactions_b67b5813")}</span>}
				</Paging>
			</Content>
		</Panel>
		{transaction && <div ref={transactionPopup} role="dialog" aria-modal="true" aria-label={msg("templatePayment_c88c6d69", { v0: transaction.id })} tabIndex={-1} onKeyDown={event => {
			event.stopPropagation();
			if (event.key === "Escape") { setTransaction(null); return; }
			if (event.key !== "Tab") return;
			const controls = transactionPopup.current?.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), [tabindex="0"]');
			const first = controls?.[0], last = controls?.[controls.length - 1];
			if (!first || !last) { event.preventDefault(); return; }
			if (event.shiftKey && (document.activeElement === first || document.activeElement === transactionPopup.current)) { event.preventDefault(); last.focus(); }
			else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
		}}>
			<TransactionPopup key={transaction.id} session={transaction} onClose={() => setTransaction(null)} onOpenSession={setTransaction} onExit={onClose} />
		</div>}
		{canManage && selection.scope === scopeKey && releaseIds && <ReleaseReservePopup sessionIds={releaseIds} projectId={projectId} userId={userId} onClose={() => setReleaseIds(null)} onSaved={() => { setReleaseIds(null); refresh(); GD.S_TRANSACTIONS_UPDATE.invoke(); }} />}
		{exportOpen && <CursorExportPopup job={exportJob} title={msg("exportHeldTransactions")} description={msg("templateRollingReserveCsv_7a4c3148", { v0: currency })} onClose={() => setExportOpen(false)} />}
	</div>, document.body);
};
